<template>
  <header class="tb">
    <button class="tb-icon" type="button" :title="collapsed ? $tr('Expand the menu') : $tr('Collapse the menu')" @click="$store.dispatch('toggleSidebar')">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="4" y1="7" x2="20" y2="7" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="17" x2="20" y2="17" /></svg>
    </button>

    <!-- Store: where the panel is looking right now -->
    <label class="tb-store" v-if="hasStores" :title="scopeTitle">
      <svg class="tb-store-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l1-5h16l1 5" /><path d="M4 9v11h16V9" /><path d="M9 20v-6h6v6" /></svg>
      <select class="tb-select" :value="selected" @change="switchStore($event.target.value)">
        <template v-if="canPickAll">
          <option :value="ALL">{{ $tr(allLabel) }}</option>
          <optgroup label="One store">
            <option v-for="s in stateStores.stores" :key="s.shash" :value="s.shash">{{ s.s_name }}</option>
          </optgroup>
        </template>
        <template v-else>
          <option v-for="s in stateStores.stores" :key="s.shash" :value="s.shash">{{ s.s_name }}</option>
        </template>
      </select>
      <span class="tb-scope" v-if="me.loaded && !me.allStores">only {{ me.stores.length }}</span>
    </label>

    <PageSearch class="tb-search" :class="{ 'is-shown': searchOpen }" />

    <div class="tb-right">
      <button class="tb-icon tb-search-toggle" type="button" title="Find a page" @click="searchOpen = !searchOpen">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
      </button>
      <!-- Who is around: filled by the chat step -->
      <span class="tb-presence" v-if="presence">{{ presence }}</span>

      <button class="tb-icon" type="button" :class="{ 'is-on': drawerOpen && drawerTab === 'approvals' }" title="Approvals" @click="$store.dispatch('toggleDrawer', 'approvals')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
        <span class="tb-badge" v-if="approvalsCount">{{ approvalsCount > 99 ? "99+" : approvalsCount }}</span>
      </button>

      <button class="tb-icon" type="button" :class="{ 'is-on': drawerOpen && drawerTab === 'chat' }" title="Team chat" @click="$store.dispatch('toggleDrawer', 'chat')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
        <span class="tb-badge" v-if="chatUnread">{{ chatUnread > 99 ? "99+" : chatUnread }}</span>
      </button>

      <button class="tb-icon" type="button" v-if="assistantAllowed" :class="{ 'is-on': drawerOpen && drawerTab === 'assistant' }" title="Connect, the assistant" @click="$store.dispatch('toggleDrawer', 'assistant')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" /><path d="M19 17l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z" /></svg>
      </button>

      <button class="tb-icon" type="button" :title="theme === 'light' ? $tr('Switch to dark') : $tr('Switch to light')" @click="$store.commit('TOGGLE_THEME')">
        <svg v-if="theme === 'light'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>
        <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
      </button>

      <!-- The panel's language: English or Turkish (src/i18n). -->
      <button class="tb-icon tb-lang" type="button" translate="no" :title="lang === 'tr' ? 'Switch to English' : 'Türkçeye geç'" @click="switchLanguage">
        <span :class="{ on: lang === 'en' }">EN</span><span :class="{ on: lang === 'tr' }">TR</span>
      </button>

      <div class="tb-user" @click="userOpen = !userOpen" ref="user">
        <span class="tb-avatar">{{ initials }}</span>
        <span class="tb-user-text">
          <span class="tb-user-name">{{ userName }}</span>
          <span class="tb-user-role">{{ me.roleName || "—" }}</span>
        </span>
        <div class="tb-menu" v-if="userOpen">
          <div class="tb-menu-head">
            <div class="tb-menu-name">{{ userName }}</div>
            <div class="tb-menu-sub">{{ me.roleName || "role not loaded" }} · {{ me.loaded && !me.allStores ? (me.stores.length === 1 ? $tr("1 location") : $trf("{0} locations", [me.stores.length])) : $tr("all locations") }}</div>
          </div>
          <button type="button" class="tb-menu-item" @click.stop="logout">Sign out</button>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { mapState } from "vuex";
import PageSearch from "./PageSearch.vue";
import { ALL_STORES_LABEL } from "@/store/modules/common/stores";
import { locale, setLocale, tr, trf } from "@/i18n";

// The <select> value of "All stores"; never a store hash.
const ALL = "*all*";

export default {
  name: "TopBar",
  components: { PageSearch },
  data() {
    return { userOpen: false, searchOpen: false, ALL, allLabel: ALL_STORES_LABEL };
  },
  computed: {
    ...mapState({
      stateStores: "stores",
      theme: (s) => s.common.theme,
      collapsed: (s) => s.panel.sidebarCollapsed,
      drawerOpen: (s) => s.panel.drawerOpen,
      drawerTab: (s) => s.panel.drawerTab,
      me: (s) => s.rbac.me,
      assistantAllowed: (s) => s.assistant.allowed,
    }),
    approvalsCount() { return this.$store.getters.approvalsCount; },
    lang() { return locale.value; },
    hasStores() { return Array.isArray(this.stateStores.stores) && this.stateStores.stores.length > 0; },
    // "All stores" adds up every store of the brand: for head office (rbac mayPickAllStores), and only when there is
    // more than one to add up.
    canPickAll() {
      return this.$store.getters.mayPickAllStores && this.$store.getters.brandStores.length > 1;
    },
    selected() {
      return this.stateStores.current.allStores ? ALL : this.stateStores.current.storeHash;
    },
    userName() { return (this.$store.getters.getUser && this.$store.getters.getUser.name) || "—"; },
    initials() {
      return String(this.userName).split(/\s+/).map((w) => w.charAt(0)).join("").slice(0, 2).toUpperCase() || "?";
    },
    scopeTitle() {
      if (this.stateStores.current.allStores) return tr("Every store of the brand, added together");
      return this.me.loaded && !this.me.allStores ? trf("Your account covers {0} of the stores", [this.me.stores.length]) : tr("Which store the panel is looking at");
    },
    presence() { return this.$store.getters.chatPresence; },
    chatUnread() { return this.$store.getters.chatUnread; },
  },
  created() {
    this._onDoc = (e) => { if (this.$refs.user && !this.$refs.user.contains(e.target)) this.userOpen = false; };
    document.addEventListener("click", this._onDoc);
  },
  beforeUnmount() {
    document.removeEventListener("click", this._onDoc);
  },
  methods: {
    switchStore(value) {
      if (value === ALL) {
        if (!this.stateStores.current.allStores) this.$store.dispatch("setAllStores");
        return;
      }
      if (value === this.stateStores.current.storeHash) return;
      this.$store.dispatch("setStoreDetails", value);
    },
    logout() {
      this.userOpen = false;
      this.$store.dispatch("logoutService");
      this.$router.push("/login");
    },
    switchLanguage() {
      setLocale(this.lang === "tr" ? "en" : "tr");
    },
  },
};
</script>

<style scoped>
.tb { position: fixed; top: 0; right: 0; left: var(--sidebar-width, 260px); z-index: 25; height: var(--topbar-height); display: flex; align-items: center; gap: var(--s-3); padding: 0 var(--s-4); background: var(--bg-sidebar); border-bottom: 1px solid var(--sidebar-border); transition: left var(--motion-base) var(--ease-out); }
.tb-search-toggle { display: none; }
.tb-icon { position: relative; width: 36px; height: 36px; display: inline-flex; align-items: center; justify-content: center; border: 0; border-radius: var(--r-md); background: transparent; color: var(--sidebar-text); cursor: pointer; transition: background var(--motion-fast) var(--ease-out), color var(--motion-fast) var(--ease-out); flex: 0 0 auto; }
.tb-icon svg { width: 18px; height: 18px; }
.tb-icon:hover { background: var(--bg-hover); color: var(--sidebar-text-hover); }
.tb-icon.is-on { background: var(--sidebar-active-bg); color: var(--sidebar-active-text); }
.tb-lang { width: auto; padding: 0 var(--s-2); gap: 2px; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; }
.tb-lang span { padding: 2px 5px; border-radius: var(--r-sm, 4px); opacity: 0.55; }
.tb-lang span.on { opacity: 1; background: var(--sidebar-active-bg); color: var(--sidebar-active-text); }
.tb-badge { position: absolute; top: 4px; right: 4px; min-width: 16px; height: 16px; padding: 0 4px; border-radius: 999px; background: var(--accent-primary); color: #fff; font-size: 10px; line-height: 16px; font-weight: 700; text-align: center; }
.tb-store { display: flex; align-items: center; gap: var(--s-2); height: 36px; padding: 0 var(--s-2) 0 var(--s-3); border: 1px solid var(--border-primary); border-radius: var(--r-md); background: var(--bg-input); max-width: 18rem; }
.tb-store-icon { width: 16px; height: 16px; color: var(--text-muted); flex: 0 0 auto; }
.tb-select { border: 0; background: transparent; color: var(--text-primary); font-size: var(--t-body-sm); font-weight: 500; font-family: inherit; outline: none; cursor: pointer; max-width: 13rem; }
.tb-select option { background: var(--bg-primary); color: var(--text-primary); }
.tb-scope { font-size: 10px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; padding: 2px 6px; border-radius: 999px; background: var(--warning-bg); color: var(--warning); white-space: nowrap; }
.tb-search { flex: 1; }
.tb-right { display: flex; align-items: center; gap: var(--s-1); margin-left: auto; }
.tb-presence { font-size: var(--t-caption); color: var(--text-muted); margin-right: var(--s-2); white-space: nowrap; }
.tb-user { position: relative; display: flex; align-items: center; gap: var(--s-2); height: 36px; padding: 0 var(--s-2) 0 var(--s-1); margin-left: var(--s-2); border-radius: var(--r-md); cursor: pointer; }
.tb-user:hover { background: var(--bg-hover); }
.tb-avatar { width: 28px; height: 28px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; background: var(--gradient-accent); color: #fff; font-size: 11px; font-weight: 700; letter-spacing: 0.02em; }
.tb-user-text { display: flex; flex-direction: column; line-height: 1.1; }
.tb-user-name { font-size: var(--t-body-sm); font-weight: 600; color: var(--text-primary); white-space: nowrap; max-width: 10rem; overflow: hidden; text-overflow: ellipsis; }
.tb-user-role { font-size: 11px; color: var(--text-muted); white-space: nowrap; }
.tb-menu { position: absolute; top: calc(100% + 6px); right: 0; min-width: 14rem; padding: var(--s-1); background: var(--bg-primary); border: 1px solid var(--border-primary); border-radius: var(--r-lg); box-shadow: var(--shadow-overlay); z-index: 60; cursor: default; }
.tb-menu-head { padding: var(--s-2) var(--s-3); border-bottom: 1px solid var(--border-light); margin-bottom: var(--s-1); }
.tb-menu-name { font-size: var(--t-body); font-weight: 600; color: var(--text-primary); }
.tb-menu-sub { font-size: var(--t-caption); color: var(--text-muted); }
.tb-menu-item { display: block; width: 100%; text-align: left; padding: var(--s-2) var(--s-3); border: 0; border-radius: var(--r-md); background: transparent; color: var(--text-primary); font-size: var(--t-body-sm); font-family: inherit; cursor: pointer; }
.tb-menu-item:hover { background: var(--bg-hover); }
@media (max-width: 900px) {
  .tb { left: 0; gap: var(--s-2); padding: 0 var(--s-2); }
  .tb-user-text, .tb-presence { display: none; }
  .tb-store { max-width: 11rem; min-width: 0; }
  .tb-select { max-width: 8.5rem; }
  .tb-user { margin-left: 0; padding: 0 var(--s-1); }
}
@media (max-width: 640px) {
  .tb-search-toggle { display: inline-flex; }
  /* The finder becomes a row under the bar, opened from the search icon. */
  .tb-search { display: none; position: absolute; top: var(--topbar-height); left: 0; right: 0; max-width: none; padding: var(--s-2); background: var(--bg-sidebar); border-bottom: 1px solid var(--sidebar-border); }
  .tb-search.is-shown { display: block; }
  .tb-scope { display: none; }
}
</style>
